<template>
  <div ref="container" class="user-tag-wrapper" :class="cssClass">
    <div
      ref="userName"
      class="user-tag"
      @mouseover="handleTagClickOrHover"
      @click="handleTagClickOrHover"
      @mouseout="handleHoverAway"
    >
      <slot name="label" />
      <svg-icon
        v-if="removable"
        class="btn-remove"
        name="Cross16"
        kind="action"
        @click="handleRemove"
      />
    </div>
    <slot name="userDetails">
      <user-details v-show="showDetails" ref="userDetails" :user="user" />
    </slot>
  </div>
</template>

<script setup lang="ts">
import UserDetails from "@plugins/user-tag/components/UserDetails.vue";
import { createPopper, Instance as PopperInstance } from "@popperjs/core";
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";

// Data
const showDetails = ref(false);

// Props
interface Props {
  showDetailOnHover?: boolean;
  showDetailOnClick?: boolean;
  user?: Record<string, unknown>;
  removable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDetailOnHover: false,
  showDetailOnClick: true,
  user: () => ({}),
  removable: false,
});

// Computed
const cssClass = computed(() => {
  return {
    removable: props.removable,
  };
});

const popperInstance = ref<PopperInstance | null>(null);
const userDetailsRef = useTemplateRef("userDetails");
const userNameRef = useTemplateRef("userName");
const containerRef = useTemplateRef("container");

const emit = defineEmits<{
  remove: [value: number];
}>();

// Methods
const handleTagClickOrHover = function (event) {
  if (
    (props.showDetailOnHover && event.type === "mouseover") ||
    (props.showDetailOnClick && event.type === "click")
  ) {
    showDetails.value = true;
  }
};

const handleClickAway = function (event) {
  if (props.showDetailOnClick && event.type === "click") {
    const container = containerRef.value;
    const clickedAway = !container?.contains(event.target);
    if (clickedAway && showDetails.value) {
      showDetails.value = false;
    }
  }
};

const handleHoverAway = function (event) {
  if (props.showDetailOnHover && event.type === "mouseout") {
    showDetails.value = false;
  }
};

const handleShowDetails = function () {
  const userName = userNameRef.value;
  const userDetails = userDetailsRef.value;

  if (userName && userDetails) {
    const options = {
      placement: "auto" as const,
      modifiers: [
        {
          name: "flip",
          options: {
            allowedAutoPlacements: ["bottom"],
          },
        },
      ],
    };
    popperInstance.value = createPopper(userName, userDetails.$el, options);
  }
};

const handleRemove = () => {
  const userId = props.user?.userId;

  if (props.removable && typeof userId === "number") {
    emit("remove", userId);
  }
};

// Watch
watch(showDetails, function () {
  if (showDetails.value) {
    handleShowDetails();
  }
});

// Mounted
onMounted(() => {
  document.addEventListener("click", handleClickAway, true);
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.user-tag-wrapper {
  position: relative;

  &.removable {
    .btn-remove {
      margin: 0 $spacing-03 0 (-$spacing-03);
      cursor: pointer;

      [fill] {
        fill: $tag-text-01;
      }
    }
  }

  .user-tag {
    display: flex;
    align-items: center;
    background-color: tint($interactive-01, 80%);
    border-radius: 20px;

    > .label-component {
      margin-left: $spacing-02;
    }
  }
}
</style>
